import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import type { AdminTenant, TenantStatus } from '@/api/models';

interface TenantStatusDialogProps {
  open: boolean;
  tenant: AdminTenant;
  onClose: () => void;
  onSaved: (tenant: AdminTenant) => void;
}

const STATUS_VALUES: TenantStatus[] = ['active', 'suspended'];

/** `PUT /v1/admin/tenants/{code}/status` — its own contract operation, never folded into the general `PATCH`. */
export function TenantStatusDialog({ open, tenant, onClose, onSaved }: TenantStatusDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [status, setStatus] = useState<TenantStatus>(tenant.status);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(tenant.status);
      setAccessDenied(false);
    }
  }, [open, tenant.status]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateAdminTenantStatus(tenant.code, { status });
      notify(translate('pay.tenants.statusDialog.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.tenants.statusDialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Alert severity="info">{translate('pay.tenants.statusDialog.body')}</Alert>
            <TextField
              select
              size="small"
              label={translate('pay.tenants.statusDialog.fieldStatus')}
              value={status}
              onChange={(event) => setStatus(event.target.value as TenantStatus)}
              fullWidth
            >
              {STATUS_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(`pay.tenants.status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.tenants.statusDialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || status === tenant.status}>
            {translate('pay.tenants.statusDialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
